/**
 * ImageMessageBubble component
 * Displays an image message in the Math Coach chat.
 * New component (not a modification of MessageBubble) to respect
 * PRD Invariant 4 — zero impact on English session flows.
 *
 * AC coverage:
 *   AC-003.3 - Image appears in chat as a sent message (optimistic blob URL or real S3 URL)
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { Message } from '@/types/session';
import tutorAvatar from '@/assets/tutor-avatar.svg';
import tutorIcon from '@/assets/tutor-icon.png';
import studentAvatar from '@/assets/student-avatar.png';

export interface ImageMessageBubbleProps {
  /** Message data — must have type='image' and imageUrl */
  message: Message;
  /** True for parent messages (right-aligned) */
  isOwnMessage: boolean;
}

/**
 * ImageMessageBubble - Chat bubble that renders an uploaded image.
 * Parent messages: right-aligned with pebbles-200 background.
 * Tutor messages (if ever): left-aligned with otter-sand background.
 */
export function ImageMessageBubble({ message, isOwnMessage }: ImageMessageBubbleProps) {
  const [tutorIconError, setTutorIconError] = useState(false);
  const [studentIconError, setStudentIconError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const tutorAvatarSrc = tutorIconError ? tutorAvatar : tutorIcon;

  const formatTimestamp = (timestamp: string): string => {
    try {
      return format(new Date(timestamp), 'HH:mm');
    } catch {
      return '';
    }
  };

  return (
    <div className={cn('flex mb-4 items-end gap-2', isOwnMessage ? 'justify-end' : 'justify-start')}>
      {/* Tutor avatar on left (only for non-own messages) */}
      {!isOwnMessage && (
        <img
          src={tutorAvatarSrc}
          alt=""
          aria-hidden="true"
          className="w-11 h-11 rounded-full flex-shrink-0 mb-1 object-cover"
          onError={() => setTutorIconError(true)}
        />
      )}

      {/* Message bubble */}
      <div
        className={cn(
          'max-w-[85%] md:max-w-[70%] rounded-lg px-4 py-2',
          isOwnMessage
            ? 'bg-pebbles-200 text-otter-earth-800 shadow-none'
            : 'bg-otter-sand text-otter-earth-800 shadow-sm'
        )}
      >
        {/* Image content */}
        {message.imageUrl && !imageError ? (
          <div className="relative">
            {/* Loading placeholder */}
            {!imageLoaded && (
              <div className="w-48 h-32 bg-otter-sand rounded-lg animate-pulse" aria-hidden="true" />
            )}
            <img
              src={message.imageUrl}
              alt="Uploaded math question"
              loading="lazy"
              className={cn(
                'max-w-full max-h-64 rounded-lg object-contain cursor-pointer',
                !imageLoaded && 'hidden'
              )}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageLoaded(true);
                setImageError(true);
              }}
            />
          </div>
        ) : (
          <p className="text-sm md:text-base text-otter-earth-500 italic">
            Image could not be loaded.
          </p>
        )}

        {/* Timestamp */}
        <span className={cn('text-xs mt-1 block', 'text-pebbles-500')}>
          {formatTimestamp(message.timestamp)}
        </span>
      </div>

      {/* Parent (own) avatar on right */}
      {isOwnMessage && (
        <img
          src={studentIconError ? studentAvatar : studentAvatar}
          alt=""
          aria-hidden="true"
          className="w-8 h-8 rounded-full flex-shrink-0 mb-1 object-cover"
          onError={() => setStudentIconError(true)}
        />
      )}
    </div>
  );
}
